import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './Header.css';

const NAV_ITEMS = [
  { to: '/courses', label: 'Courses', emoji: '\uD83D\uDCDA' },
  { to: '/dashboard', label: 'Dashboard', emoji: '\uD83D\uDE80' },
  { to: '/achievements', label: 'Trophies', emoji: '\uD83C\uDFC6' },
  { to: '/ask-sage', label: 'Ask Sage', emoji: '\uD83E\uDDD9' },
  { to: '/pricing', label: 'Pricing', emoji: '\u2B50' },
];

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Track scroll for shadow effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return (
    <>
      <header className={`header${scrolled ? ' header--scrolled' : ''}`}>
        <div className="header__inner">
          {/* Logo */}
          <Link to="/" className="header__logo" onClick={closeMenu}>
            <span className="header__logo-icon" role="img" aria-label="Graduation cap">
              \uD83C\uDF93
            </span>
            <span className="header__logo-text">MiskTech University</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="header__nav" aria-label="Main navigation">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `header__nav-link${isActive ? ' active' : ''}`
                }
              >
                <span className="header__nav-link-emoji" role="img" aria-hidden="true">
                  {item.emoji}
                </span>
                {item.label}
              </NavLink>
            ))}
            <Link to="/parent" className="header__cta">
              Parent Portal
            </Link>
          </nav>

          {/* Hamburger Button */}
          <button
            className={`header__hamburger${menuOpen ? ' header__hamburger--open' : ''}`}
            onClick={toggleMenu}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="header__hamburger-bar" />
            <span className="header__hamburger-bar" />
            <span className="header__hamburger-bar" />
          </button>
        </div>
      </header>

      {/* Mobile Overlay */}
      <div
        className={`header__mobile-overlay${menuOpen ? ' header__mobile-overlay--visible' : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      {/* Mobile Menu */}
      <nav
        className={`header__mobile-menu${menuOpen ? ' header__mobile-menu--open' : ''}`}
        aria-label="Mobile navigation"
      >
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `header__mobile-link${isActive ? ' active' : ''}`
            }
            onClick={closeMenu}
          >
            <span className="header__mobile-link-emoji" role="img" aria-hidden="true">
              {item.emoji}
            </span>
            {item.label}
          </NavLink>
        ))}
        <div className="header__mobile-divider" />
        <Link to="/parent" className="header__mobile-cta" onClick={closeMenu}>
          Parent Portal
        </Link>
      </nav>
    </>
  );
}

export default Header;
